/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { PriceComponent } from '@msdyn365-commerce/components';
import MsDyn365, { IComponentProps, ICoreContext, IImageSettings, Image } from '@msdyn365-commerce/core';
import { ProductListLine, ProductPrice } from '@msdyn365-commerce/retail-proxy';
import { getFallbackImageUrl, getProductPageUrlSync, ObjectExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Button, IncrementalQuantity, isMobile, VariantType } from '@msdyn365-commerce-modules/utilities';
import React, { useState } from 'react';

import { QuickOrderGridProduct } from '../../quick-order-grid-product';
import { IQuickOrderGridRowCallbacks } from '../../utilities';

/**
 * Data required to render each quick order grid row.
 */
export interface IQuickOrderGridTableRowData {
    product: QuickOrderGridProduct;
}

/**
 * Resources for quick order grid table rows.
 */
export interface IQuickOrderGridTableRowResources {
    quickOrderGridQuantityMobileText: string;
    quickOrderGridProductDimensionsSeparator: string;
    quickOrderGridViewDetailsButtonText: string;
    quickOrderGridViewDetailsButtonAriaLabel: string;
    quickOrderGridRemoveItemButtonAriaLabel: string;
    quickOrderGridInputQuantityAriaLabel: string;
    quickOrderGridDecrementButtonAriaLabel: string;
    quickOrderGridIncrementButtonAriaLabel: string;
    originalPriceText: string;
    currentPriceText: string;
    freePriceText: string;
}

/**
 * Interface for quick order grid table props.
 */
export interface IQuickOrderGridTableRowProps extends IComponentProps<IQuickOrderGridTableRowData> {
    context: ICoreContext;
    className: string;
    productListLine: ProductListLine;
    price?: ProductPrice;
    moduleId: string;
    moduleTypeName: string;
    quantity: number;
    imageSettings?: IImageSettings;
    isMobile: boolean;
    resources: IQuickOrderGridTableRowResources;
    index: number;
    callbacks: IQuickOrderGridRowCallbacks;
}

/**
 * The default image settings for quick order grid table row.
 */
const defaultImageSettings: IImageSettings = {
    viewports: {
        xs: { q: 'w=80&h=80&m=6', w: 0, h: 0 },
        lg: { q: 'w=147&h=147&m=6', w: 0, h: 0 }
    },
    lazyload: true,
    cropFocalRegion: true
};

/**
 * Renders product image for quick order grid row.
 * @param props - Quick order table row props.
 * @returns React element.
 */
const QuickOrderGridTableRowProductImageComponent: React.FC<IQuickOrderGridTableRowProps> = (props: IQuickOrderGridTableRowProps) => {
    const product = props.data.product.productLine.fullProduct?.ProductDetails;
    const fallbackImageUrl = getFallbackImageUrl(product?.ItemId, props.context.actionContext.requestContext.apiSettings);

    const containerClassName = `${props.className}__product-image-container`;

    return (
        <div className={containerClassName}>
            <Image
                requestContext={props.context.actionContext.requestContext}
                className={`${containerClassName}__product-image`}
                altText={product?.Name}
                src={product?.PrimaryImageUrl ?? ''}
                fallBackSrc={fallbackImageUrl}
                gridSettings={props.context.request.gridSettings!}
                imageSettings={props.imageSettings ?? defaultImageSettings}
                loadFailureBehavior='empty'
            />
        </div>
    );
};

let setShowPreviousActions: React.Dispatch<React.SetStateAction<boolean>> | undefined;

/**
 * Renders product price.
 * @param props - Props for price component.
 * @returns React element.
 */
const renderProductPrice = (props: IQuickOrderGridTableRowProps): JSX.Element | null => {
    const { context, moduleId, moduleTypeName, resources } = props;
    const price = props.price;

    if (price) {
        return (
            <PriceComponent
                id={moduleId}
                typeName={moduleTypeName}
                data={{ price }}
                className='ms-quick-order-grid-row__product-price'
                freePriceText={resources.freePriceText}
                originalPriceText={resources.originalPriceText}
                currentPriceText={resources.currentPriceText}
                context={context}
            />
        );
    }
    return null;
};

/**
 * Renders product price.
 * @param props - Props for price component.
 * @returns React element.
 */
const renderUnitOfMeasure = (props: IQuickOrderGridTableRowProps): JSX.Element | null => {
    const price = props.price;

    if (price) {
        return (
            <div className='ms-quick-order-grid-row__product-unit-of-measure'>
                {StringExtensions.isNullOrEmpty(props.productListLine.UnitOfMeasure) || price.UnitOfMeasure}
            </div>
        );
    }
    return null;
};

/**
 * On Change function.
 * @param onChangeQuantity -On change quantity function.
 * @param itemKey -String.
 * @param props -Order template line props.
 * @returns Set change quantity.
 */
const onChangeHandler = (
    onChangeQuantity: (
        orderTemplateLineId: string | undefined,
        quantity: number,
        props?: IQuickOrderGridTableRowProps | undefined
    ) => boolean,
    itemKey: string | undefined,
    props: IQuickOrderGridTableRowProps
) => (newValue: number) => {
    onChangeQuantity(itemKey, newValue, props);
    return true;
};

/**
 * On Change function.
 * @param onRemoveItem -On change quantity function.
 * @param quickOrderGridProductKey -QuickOrderGridp Product.
 * @param props -Order template line props.
 * @returns Set change quantity.
 */
const onRemoveHandler = (
    onRemoveItem: (quickOrderGridProduct: QuickOrderGridProduct, props?: IQuickOrderGridTableRowProps | undefined) => void,
    quickOrderGridProductKey: QuickOrderGridProduct,
    props: IQuickOrderGridTableRowProps
) => () => {
    onRemoveItem(quickOrderGridProductKey, props);
};

/**
 * Renders actions popup for the mobile view.
 * @param props - Configuration for popup.
 * @returns A react node with actions..
 */
const QuickOrderGridExtraActionsPopupComponent: React.FC<IQuickOrderGridTableRowProps> = (
    props: IQuickOrderGridTableRowProps
): JSX.Element | null => {
    const {
        resources: { quickOrderGridViewDetailsButtonAriaLabel, quickOrderGridViewDetailsButtonText }
    } = props;
    const product = props.data.product.productLine.fullProduct?.ProductDetails;
    const [shouldShowActions, setShowActions] = useState(false);
    let productUrl = '';
    if (product === undefined) {
        return null;
    }
    if (!StringExtensions.isNullOrWhitespace(product.Name) && !ObjectExtensions.isNullOrUndefined(product.RecordId)) {
        productUrl = getProductPageUrlSync(product.Name!, product.RecordId, props.context.actionContext);
    }

    const onClickViewDetails = React.useCallback(() => {
        if (MsDyn365.isBrowser) {
            window.location.href = productUrl;
        }
    }, [productUrl]);

    const toggle = React.useCallback(() => {
        const shouldShowActionsNewValue = !shouldShowActions;
        if (shouldShowActionsNewValue) {
            if (setShowPreviousActions) {
                setShowPreviousActions(false);
            }
            setShowActions(shouldShowActionsNewValue);
            setShowPreviousActions = setShowActions;
        } else {
            setShowPreviousActions = undefined;
            setShowActions(shouldShowActionsNewValue);
        }
    }, [shouldShowActions, setShowActions]);

    if (StringExtensions.isNullOrWhitespace(productUrl)) {
        return null;
    }

    const className = `${props.className}__extra-actions-cell`;
    const actionsContainerClassName = `${className}__actions-container`;

    return (
        <div className={className}>
            <Button className={`${className}__toggle`} onClick={toggle} />
            {shouldShowActions && (
                <div className={actionsContainerClassName}>
                    <Button
                        className={`${actionsContainerClassName}__view-details`}
                        onClick={onClickViewDetails}
                        aria-label={quickOrderGridViewDetailsButtonAriaLabel}
                    >
                        {quickOrderGridViewDetailsButtonText}
                    </Button>
                    <Button
                        className={`${props.className}__product-remove`}
                        aria-label={props.resources.quickOrderGridRemoveItemButtonAriaLabel}
                    >
                        {props.resources.quickOrderGridRemoveItemButtonAriaLabel}
                    </Button>
                </div>
            )}
        </div>
    );
};

/**
 * Renders quick order grid table rows for desktop view.
 * @param props - Configuration for the rows.
 * @returns A react node with a list of quick order grid rows for desktop view.
 */
export const QuickOrderGridDesktopTableRowCellComponent: React.FC<IQuickOrderGridTableRowProps> = (props: IQuickOrderGridTableRowProps) => {
    const product = props.data.product.productLine.fullProduct?.ProductDetails;
    const productListLine = props.data.product.productLine.productListLine;
    const { quantity } = props;
    const columnWithNumberClassName = `${props.className}__column-with-number`;
    if (product !== undefined) {
        let productUrl = '';
        if (!StringExtensions.isNullOrWhitespace(product.Name) && !ObjectExtensions.isNullOrUndefined(product.RecordId)) {
            productUrl = getProductPageUrlSync(product.Name!, product.RecordId, props.context.actionContext);
        }

        return (
            <>
                <td>
                    <QuickOrderGridTableRowProductImageComponent {...props} />
                </td>
                <td>{product.ItemId}</td>
                <td>
                    <div className={`${props.className}__product-info`}>
                        <a className={`${props.className}__product-name`} href={productUrl}>
                            {product.Name}
                        </a>
                        <span className={`${props.className}__product-dimensions`}>
                            {product.Dimensions?.map(dimension => dimension.DimensionValue?.Value)
                                .filter(dimensionValue => !StringExtensions.isNullOrWhitespace(dimensionValue))
                                .join(props.resources.quickOrderGridProductDimensionsSeparator)}
                        </span>
                    </div>
                </td>
                <td className={columnWithNumberClassName}>{renderProductPrice(props)}</td>
                <td>{renderUnitOfMeasure(props)}</td>
                <td className={`${props.className}__product-quantity`}>
                    <IncrementalQuantity
                        id={`msc-quick-order-grid-row__quantity_${product.RecordId}-${productListLine.LineId ?? ''}`}
                        max={10}
                        currentCount={quantity}
                        inputQuantityAriaLabel={props.resources.quickOrderGridInputQuantityAriaLabel}
                        decrementButtonAriaLabel={props.resources.quickOrderGridDecrementButtonAriaLabel}
                        incrementButtonAriaLabel={props.resources.quickOrderGridIncrementButtonAriaLabel}
                        key={productListLine.LineId}
                        isGiftCard={product.IsGiftCard}
                        onChange={onChangeHandler(props.callbacks.onChangeQuantity, productListLine.LineId, props)}
                    />
                </td>
                <td>
                    <Button
                        className={`${props.className}__product-remove`}
                        aria-label={props.resources.quickOrderGridRemoveItemButtonAriaLabel}
                        onClick={onRemoveHandler(props.callbacks.onRemoveItem, props.data.product, props)}
                    />
                </td>
            </>
        );
    }

    return null;
};

/**
 * Renders rows for mobile view.
 * @param props - Configuration for the rows.
 * @returns A react node with a list of quick order grid rows for mobile view.
 */
export const QuickOrderGridMobileTableRowCellComponent: React.FC<IQuickOrderGridTableRowProps> = (props: IQuickOrderGridTableRowProps) => {
    const product = props.data.product.productLine.fullProduct?.ProductDetails;
    const productListLine = props.data.product.productLine.productListLine;
    const { quantity } = props;
    if (product !== undefined) {
        return (
            <>
                <td>
                    <QuickOrderGridTableRowProductImageComponent {...props} />
                </td>
                <td>
                    <div className={`${props.className}__product-info`}>
                        <span className={`${props.className}__product-name`}>{product.RecordId}</span>
                        <span className={`${props.className}__product-quantity`}>
                            <IncrementalQuantity
                                id={`msc-quick-order-grid-row__quantity_${product.RecordId}-${productListLine.LineId ?? ''}`}
                                max={10}
                                currentCount={quantity}
                                inputQuantityAriaLabel={props.resources.quickOrderGridInputQuantityAriaLabel}
                                decrementButtonAriaLabel={props.resources.quickOrderGridDecrementButtonAriaLabel}
                                incrementButtonAriaLabel={props.resources.quickOrderGridIncrementButtonAriaLabel}
                                key={productListLine.LineId}
                                isGiftCard={product.IsGiftCard}
                                onChange={onChangeHandler(props.callbacks.onChangeQuantity, productListLine.LineId, props)}
                            />
                        </span>
                    </div>
                </td>
                <td>
                    <QuickOrderGridExtraActionsPopupComponent {...props} />
                </td>
            </>
        );
    }
    return null;
};

/**
 * Quick order table row component.
 * @param props - Configuration of the component.
 * @returns React node for the row.
 */
export const QuickOrderGridTableRowComponent: React.FC<IQuickOrderGridTableRowProps> = (props: IQuickOrderGridTableRowProps) => {
    const rowClassName = `${props.className}__row`;
    const isMobileView = isMobile({ variant: VariantType.Viewport, context: props.context.request }) === 'xs';
    let headerCells: JSX.Element;
    if (isMobileView) {
        headerCells = <QuickOrderGridMobileTableRowCellComponent {...props} />;
    } else {
        headerCells = <QuickOrderGridDesktopTableRowCellComponent {...props} />;
    }

    return (
        <thead className={props.className}>
            <tr className={rowClassName}>{headerCells}</tr>
        </thead>
    );
};
